import { useCallback, useEffect, useState } from 'react';
import SystemArchitecture from '../components/system/SystemArchitecture';
import SystemGateRail from '../components/system/SystemGateRail';
import SystemMarketCards from '../components/system/SystemMarketCards';
import SystemWatchlistPanel from '../components/system/SystemWatchlistPanel';
import { CrossMarketStatus, fetchCrossMarketStatus } from '../services/crossMarketApi';
import './SystemOverview.css';

type Scope = 'ALL' | 'US' | 'KR';
const SCOPES: { key: Scope; label: string }[] = [
  { key: 'ALL', label: '전체' }, { key: 'US', label: 'US' }, { key: 'KR', label: 'KR' },
];

export default function SystemOverview() {
  const [scope, setScope] = useState<Scope>('ALL');
  const [data, setData] = useState<CrossMarketStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchCrossMarketStatus());
    } catch (err) {
      setError(err instanceof Error ? err.message : '시스템 현황 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <PageState text="새 시스템 현황 로딩 중..." />;
  if (error && !data) return <PageState text={`API 오류: ${error}`} error onRetry={load} />;
  if (!data) return null;

  const liveStatus = liveStatusText(data);
  const locked = !isUsLive(data) && !isKrLive(data);
  return (
    <div className="system-overview-page">
      <header className="system-header">
        <div>
          <span className="system-kicker">TOSS TRADING OS</span>
          <h1>새 시스템 현황</h1>
          <p>US live_pilot, KR 잠금, Toss 검증 universe와 주문 안전장치를 한눈에 봅니다.</p>
        </div>
        <button className="system-refresh" onClick={load} disabled={loading}>
          {loading ? '갱신 중...' : '새로고침'}
        </button>
      </header>

      <section className={`safety-banner${locked ? ' locked' : ' unlocked'}`}>
        <div><span className="safety-dot" /><strong>{liveStatus.title}</strong></div>
        <p>{liveStatus.detail} · Toss 읽기 전용 {data.safety.toss_read_only_enabled ? '활성' : '비활성'} · Crypto 폐기 · Analyst 주문 영향 없음</p>
      </section>
      {error && <div className="system-state error-state">최신 조회 실패: {error}</div>}

      <nav className="market-tabs" aria-label="시장 선택">
        {SCOPES.map((item) => (
          <button key={item.key} className={scope === item.key ? 'active' : ''} onClick={() => setScope(item.key)}>
            {item.label}
          </button>
        ))}
      </nav>

      <SystemMarketCards scope={scope} us={data.markets.US} kr={data.markets.KR} safety={data.safety} />
      <SystemWatchlistPanel />
      <SystemGateRail gates={data.gates} />
      <SystemArchitecture
        analystDescription={data.analyst.description}
        brokerDetail={brokerDetail(data)}
      />
      <p className="system-generated">실제 운영 데이터 기준 · {new Date(data.generated_at).toLocaleString('ko-KR')}</p>
    </div>
  );
}

function liveStatusText(data: CrossMarketStatus) {
  const us = isUsLive(data);
  const kr = isKrLive(data);
  if (us && kr) return { title: 'US/KR LIVE 주문 가능', detail: '현재 LIVE · US/KR switch 활성' };
  if (us) return { title: 'US LIVE 활성 · KR 잠금', detail: '현재 LIVE · Toss US live_pilot 활성' };
  if (kr) return { title: 'KR LIVE 활성 · US 잠금', detail: '현재 LIVE · Toss KR live_pilot 활성' };
  return { title: 'LIVE 주문 잠금', detail: `현재 ${data.safety.trading_mode.toUpperCase()} · 시장별 live switch 잠금` };
}

function brokerDetail(data: CrossMarketStatus) {
  if (isUsLive(data)) return '현재 Toss US Live Pilot';
  if (isKrLive(data)) return '현재 Toss KR Live Pilot';
  return data.safety.stock_live_route === 'toss' ? '현재 Toss route 잠금' : '현재 paper mode · Toss 주문 잠금';
}

function isUsLive(data: CrossMarketStatus) {
  const safety = data.safety;
  return safety.trading_mode === 'live'
    && safety.live_trading_enabled
    && safety.toss_live_market === 'US'
    && safety.toss_us_live_enabled;
}

function isKrLive(data: CrossMarketStatus) {
  const safety = data.safety;
  return safety.trading_mode === 'live'
    && safety.live_trading_enabled
    && safety.toss_live_market === 'KR'
    && safety.toss_kr_live_enabled;
}

function PageState({ text, error = false, onRetry }: { text: string; error?: boolean; onRetry?: () => void }) {
  return <div className="system-overview-page"><div className={`system-state${error ? ' error-state' : ''}`}>{text}</div>{onRetry && <button className="system-refresh" onClick={onRetry}>다시 시도</button>}</div>;
}
